import { useTranslation } from "react-i18next";

import { checkIfTranslationExists, TRANSLATABLE_OBJECTS } from "@/shared";
import { generateTranslatedText } from "@/shared/functions/functions";
import { useDateTime } from "@/shared/hooks/useDateTime";
import { useNow } from "@/shared/hooks/useNow";
import { useRelativeDateTime } from "@/shared/hooks/useRelativeDateTime";

import { SCHEDULE_CARDS_STATUSES, SCHEDULE_TIMINGS } from "../../types/enums";
import {
  DownloadMonitoringVehicleData,
  ScheduleProgress,
  SingleSchedule
} from "../../types/interfaces";

import SingleCard from "./SingleCard";

interface CardData {
  status: SCHEDULE_CARDS_STATUSES;
  badgeText: string;
  failReason?: string | null;
  nextAttempt?: string;
  currentStage?: string | null;
}

interface ScheduleStatusCardsProps {
  liveStatusData: DownloadMonitoringVehicleData;
  isBlocked: boolean;
}

const ScheduleStatusCards = ({
  liveStatusData,
  isBlocked
}: ScheduleStatusCardsProps) => {
  const { t, i18n } = useTranslation();
  const { formattedDate } = useDateTime();

  const { getDifferenceFromCurrentUTCInMinutes, formatRelativeFutureDateTime } =
    useRelativeDateTime();

  const now = useNow(60_000);

  const {
    schedules: { schedules },
    eligibility,
    download
  } = liveStatusData || {};

  const getFailReason = (reason: string | null | undefined) =>
    checkIfTranslationExists(
      String(reason),
      TRANSLATABLE_OBJECTS.DownloadMonitoring,
      i18n
    );

  // Card data for a schedule that has its own entry in download.schedulesProgress.
  // It overrides the vehicle-level download status for that schedule only.
  const getScheduleProgressCardData = ({isFailed, status, details}: ScheduleProgress): CardData => {
    if (isFailed) {
      return {
        status: SCHEDULE_CARDS_STATUSES.Failed,
        badgeText: t("General##Failed"),
        nextAttempt: t("Download monitoring##schedules##on device connection"),
        failReason: getFailReason(details ?? status)
      };
    }

    return {
      status: SCHEDULE_CARDS_STATUSES.Successful,
      badgeText: t("General##Successful")
    };
  };

  const getCardData = (singleSchedule: SingleSchedule): CardData => {
    const scheduleProgress = download?.schedulesProgress?.find(
      ({ id }) => id === singleSchedule.scheduleId
    );

    if (scheduleProgress && scheduleProgress.status !== null) {
      return getScheduleProgressCardData(scheduleProgress);
    }

    const {
      lastSuccessfulDownloadAt,
      lastAttemptFailed,
      timing,
      dueDate,
      failureDetails,
      failureReason
    } = singleSchedule;

    const lastSuccessfulDifferenceInMinutes =
      getDifferenceFromCurrentUTCInMinutes(lastSuccessfulDownloadAt, now);

    const timeUntilScheduleIsDue = formatRelativeFutureDateTime(dueDate, now);

    const isInProgress = timing === SCHEDULE_TIMINGS.InProgress;
    const isAwaitingConnection = timing === SCHEDULE_TIMINGS.AwaitingConnection;
    const isUpcoming = timing === SCHEDULE_TIMINGS.Upcoming;

    if (isInProgress) {
      return {
        status: SCHEDULE_CARDS_STATUSES.InProgress,
        badgeText: t("General##in progress"),
        currentStage: download.stage
      };
    }

    if (
      lastSuccessfulDifferenceInMinutes !== undefined &&
      lastSuccessfulDifferenceInMinutes <= 10
    ) {
      return {
        status: SCHEDULE_CARDS_STATUSES.Successful,
        badgeText: t("General##Successful")
      };
    }

    if (lastAttemptFailed) {
      return {
        status: SCHEDULE_CARDS_STATUSES.Failed,
        badgeText: t("General##Failed"),
        nextAttempt: t("Download monitoring##schedules##on device connection"),
        failReason: getFailReason(failureDetails ?? failureReason)
      };
    }

    if (isAwaitingConnection && !eligibility.isEligible) {
      return {
        status: SCHEDULE_CARDS_STATUSES.Failed,
        badgeText: t("General##Failed"),
        failReason: t(
          "Download monitoring##schedules##fail reason##download requirements not met"
        )
      };
    }

    if (isBlocked) {
      return {
        status: SCHEDULE_CARDS_STATUSES.Blocked,
        badgeText: t("General##Blocked")
      };
    }

    if (eligibility.isEligible &&
      isAwaitingConnection
    ) {
      return {
        status: SCHEDULE_CARDS_STATUSES.InProgress,
        badgeText: t("General##Awaiting connection")
      };
    }

    if (isUpcoming && !eligibility.isEligible) {
      return {
        status: SCHEDULE_CARDS_STATUSES.RequirementNotMet,
        badgeText: timeUntilScheduleIsDue
      };
    }

    if (isUpcoming) {
      return {
        status: SCHEDULE_CARDS_STATUSES.Neutral,
        badgeText: timeUntilScheduleIsDue
      };
    }

    return {
      status: SCHEDULE_CARDS_STATUSES.Neutral,
      badgeText: timeUntilScheduleIsDue
    };
  };

  return (
    <>
      {schedules.map(singleSchedule => {
        const { dueDate, name, scheduleId } = singleSchedule;

        const isOccurringSchedule = Boolean(scheduleId);

        const scheduleTitle =
          isOccurringSchedule && name
            ? generateTranslatedText(name, i18n, t)
            : t("Table##Automatic initial Vehicle Download");

        const showDueDate = Boolean(dueDate);

        const { badgeText, status, failReason, nextAttempt, currentStage } =
          getCardData(singleSchedule);

        return (
          <SingleCard
            key={scheduleId ?? "initial-download-schedule"}
            cardStatus={status}
            cardTitle={scheduleTitle}
            badgeText={badgeText}
            nextAttempt={nextAttempt}
            dueDate={showDueDate ? formattedDate(dueDate) : undefined}
            failReason={failReason}
            currentStage={currentStage}
          />
        );
      })}
    </>
  );
};

export default ScheduleStatusCards;
